import dayjs from 'dayjs';

import {
  type MarketingRouterMetricResult,
  type MarketingRouterSignificance,
  getMarketingRouterTreatmentConfig,
} from '@luxuryescapes/contract-svc-promo/marketing-router/admin';

import type { MarketingRouterRule } from '~/types/marketingRouterRule';

import { MATCH_FIELD_LABELS, toMatchValues } from './marketingRouterMatch';
import {
  METRIC_DISPLAY,
  type MarketingRouterMetricKey,
  formatTrafficAllocation,
} from './marketingRouterSignificanceDisplay';

/**
 * Pure display logic for comparing several marketing router rules side by
 * side. Each rule stays its own experiment here: nothing in this module pools
 * visitors, re-runs a test or recomputes a lift across rules. Every number it
 * returns is one svc-promo already reported for that rule, against that rule's
 * own control.
 */

/** A rule compared against itself is not a comparison. */
export const MIN_COMPARED_RULES = 2;

/**
 * Above this the side-by-side table stops fitting on a screen, and a
 * comparison nobody can read is worse than one they have to run twice.
 */
export const MAX_COMPARED_RULES = 4;

/** The query parameter the comparison page reads its selection from. */
export const COMPARISON_RULES_PARAM = 'rules';

export const MARKETING_ROUTER_RULES_PATH = '/marketing/marketing-router-rules';

export const MARKETING_ROUTER_COMPARE_PATH = `${MARKETING_ROUTER_RULES_PATH}/compare`;

/** The rule fields the comparison reads. `MarketingRouterRuleRecord` satisfies it. */
export type ComparedRule = Pick<
  MarketingRouterRule,
  'id' | 'name' | 'match' | 'start_date' | 'end_date' | 'significance_metrics' | 'variants'
> & {
  marketingRouterId: string;
  /**
   * Router-level targeting the rule inherits. Two rules can match on exactly
   * the same fields and still have run on different audiences or windows, so
   * the setup comparison reads these beside the rule's own fields.
   */
  region: string | null;
  routerStartDate: string | null;
  routerEndDate: string | null;
};

export type ComparisonEntry = {
  rule: ComparedRule;
  /** Undefined while the rule's results are still loading, or failed to load. */
  significance?: MarketingRouterSignificance;
};

/**
 * The selected rule ids, in the order they were selected, de-duplicated and
 * capped. Order is meaningful: it is the left-to-right order of the columns,
 * and the first rule is the one the default metric comes from.
 */
export function parseComparisonRuleIds(searchParams: URLSearchParams): Array<string> {
  const raw = searchParams.get(COMPARISON_RULES_PARAM) ?? '';
  const ids = raw
    .split(',')
    .map((id) => id.trim())
    .filter((id) => id !== '');

  return [...new Set(ids)].slice(0, MAX_COMPARED_RULES);
}

/**
 * Puts a selection into a set of list params, replacing whatever they carry.
 * The rules list keeps its filters in the URL too, so the two have to be
 * written together or setting a filter drops the selection.
 */
export function withComparisonSelection(params: URLSearchParams, ruleIds: Array<string>): URLSearchParams {
  const next = new URLSearchParams(params);

  if (ruleIds.length === 0) {
    next.delete(COMPARISON_RULES_PARAM);
  } else {
    next.set(COMPARISON_RULES_PARAM, ruleIds.join(','));
  }

  return next;
}

function toSelectionPath(path: string, ruleIds: Array<string>): string {
  const params = withComparisonSelection(new URLSearchParams(), ruleIds);
  const query = params.toString();

  return query === '' ? path : `${path}?${query}`;
}

/** The link that opens a comparison of these rules. */
export function toComparisonPath(ruleIds: Array<string>): string {
  return toSelectionPath(MARKETING_ROUTER_COMPARE_PATH, ruleIds);
}

/**
 * Back to the list with the selection still ticked, so a comparison can be
 * adjusted by a rule rather than rebuilt from nothing.
 */
export function toRulesListPath(ruleIds: Array<string>): string {
  return toSelectionPath(MARKETING_ROUTER_RULES_PATH, ruleIds);
}

export function formatDateRange(startDate: string, endDate: string): string {
  return `${dayjs(startDate).format('DD MMM YYYY')} – ${dayjs(endDate).format('DD MMM YYYY')}`;
}

/** The router's own window, which may be open at either end. */
function formatRouterDateRange(startDate: string | null, endDate: string | null): string {
  if (startDate === null && endDate === null) {
    return 'Always';
  }

  const from = startDate === null ? 'Open' : dayjs(startDate).format('DD MMM YYYY');
  const to = endDate === null ? 'Open' : dayjs(endDate).format('DD MMM YYYY');

  return `${from} – ${to}`;
}

function formatMetricNames(metrics: Array<MarketingRouterMetricKey>): string {
  return metrics.length === 0 ? 'None' : metrics.map((metric) => METRIC_DISPLAY[metric].name).join(', ');
}

/**
 * Each variant with what it does, because "3 variants" on its own does not say
 * whether two rules are running the same treatment on different audiences or
 * different treatments entirely.
 */
function formatVariantTreatments(variants: ComparedRule['variants']): string {
  return variants
    .map((variant) => {
      const treatments = variant.treatments ?? [];
      const applied =
        treatments.length === 0
          ? 'no change'
          : treatments
              .map((treatment) => getMarketingRouterTreatmentConfig(treatment.type)?.typeName ?? treatment.type)
              .join(', ');

      return `${variant.variant_name}: ${applied}`;
    })
    .join(' · ');
}

function formatVariantNames(variants: ComparedRule['variants']): string {
  return variants.map((variant) => `${variant.variant_name}${variant.is_control ? ' (control)' : ''}`).join(', ');
}

export type ComparisonDefinitionRow = {
  label: string;
  /** One value per compared rule, in the order they were selected. */
  values: Array<string>;
  /** Whether the rules disagree here — the rows worth reading first. */
  differs: boolean;
};

type Aspect = {
  label: string;
  valueOf: (entry: ComparisonEntry) => string;
  /** When absent the row is always shown, agreed on or not. */
  isShown?: (entries: Array<ComparisonEntry>) => boolean;
};

/**
 * Match fields become rows only when at least one rule constrains them: an
 * unset field targets everything, and a row of "Any" against "Any" reads as if
 * it had been configured on both.
 */
const MATCH_ASPECTS: Array<Aspect> = MATCH_FIELD_LABELS.map(([field, label]) => ({
  label,
  valueOf: (entry) => {
    const values = toMatchValues(entry.rule.match[field]);

    return values.length === 0 ? 'Any' : values.join(', ');
  },
  isShown: (entries) => entries.some((entry) => toMatchValues(entry.rule.match[field]).length > 0),
}));

const ASPECTS: Array<Aspect> = [
  { label: 'Date range', valueOf: (entry) => formatDateRange(entry.rule.start_date, entry.rule.end_date) },
  // The router's region and window narrow every rule on it, so two rules that
  // match identically can still have run on different audiences.
  {
    label: 'Router region',
    valueOf: (entry) => entry.rule.region ?? 'Any',
    isShown: (entries) => entries.some((entry) => entry.rule.region !== null),
  },
  {
    label: 'Router dates',
    valueOf: (entry) => formatRouterDateRange(entry.rule.routerStartDate, entry.rule.routerEndDate),
    isShown: (entries) =>
      entries.some((entry) => entry.rule.routerStartDate !== null || entry.rule.routerEndDate !== null),
  },
  ...MATCH_ASPECTS,
  { label: 'Variants', valueOf: (entry) => formatVariantNames(entry.rule.variants) },
  {
    label: 'Traffic allocation',
    valueOf: (entry) => formatTrafficAllocation(entry.rule.variants.map((variant) => variant.weight)),
  },
  { label: 'Treatments', valueOf: (entry) => formatVariantTreatments(entry.rule.variants) },
  { label: 'Decision metrics', valueOf: (entry) => formatMetricNames(entry.rule.significance_metrics) },
  {
    label: 'Control',
    valueOf: (entry) =>
      entry.significance?.baseVariantName ??
      entry.rule.variants.find((variant) => variant.is_control)?.variant_name ??
      '—',
  },
];

/**
 * The setup of every compared rule as table rows, one column per rule.
 *
 * `differs` is the whole point of the page: rules that were configured
 * differently can still be compared, but the reader has to be told where they
 * part company before they read the numbers.
 */
export function getComparisonDefinitionRows(entries: Array<ComparisonEntry>): Array<ComparisonDefinitionRow> {
  return ASPECTS.filter((aspect) => aspect.isShown?.(entries) ?? true).map((aspect) => {
    const values = entries.map((entry) => aspect.valueOf(entry));

    return { label: aspect.label, values, differs: values.some((value) => value !== values[0]) };
  });
}

/** The labels of the rows the rules disagree on, for the warning above the table. */
export function getComparisonDifferences(entries: Array<ComparisonEntry>): Array<string> {
  return getComparisonDefinitionRows(entries)
    .filter((row) => row.differs)
    .map((row) => row.label);
}

/**
 * Every metric any compared rule reports, in the order they first appear.
 * A metric only some rules measure still belongs here: the page says which
 * rules do not measure it rather than hiding the choice.
 */
export function getComparableMetrics<Entry extends Pick<ComparisonEntry, 'significance'>>(
  entries: Array<Entry>,
): Array<MarketingRouterMetricKey> {
  const seen = new Set<MarketingRouterMetricKey>();
  const ordered: Array<MarketingRouterMetricKey> = [];

  for (const entry of entries) {
    for (const result of entry.significance?.results ?? []) {
      if (!seen.has(result.metric)) {
        seen.add(result.metric);
        ordered.push(result.metric);
      }
    }
  }

  return ordered;
}

/**
 * What the page opens on: the first rule's primary metric, so the comparison
 * starts on the metric that rule is decided by. Falls back to the first metric
 * anything reports.
 */
export function getDefaultComparisonMetric(entries: Array<ComparisonEntry>): MarketingRouterMetricKey | undefined {
  const metrics = getComparableMetrics(entries);
  const primary = entries[0]?.significance?.decisions[0]?.primaryMetric;

  return primary !== undefined && metrics.includes(primary) ? primary : metrics[0];
}

export type ComparisonVariantRow = {
  variantId: string;
  variantName: string;
  isControl: boolean;
  visitors: number;
  /** The metric per visitor: a purchase rate, or TTV/margin per visitor. */
  value: number;
  /** The metric's total over those visitors. */
  total: number;
  /**
   * Lift against this rule's own control, exactly as svc-promo reported it.
   * Null on the control row, which is the baseline, and where the backend
   * could not compute one.
   */
  relativeLift: number | null;
  /** The reported test for this variant; null on the control row. */
  result: MarketingRouterMetricResult | null;
};

/**
 * One rule's variants on one metric: its control first, then its treatments.
 *
 * The control's numbers are read off the first result rather than summed from
 * the treatments — svc-promo reports `baseN`/`baseValue`/`baseTotal` on every
 * comparison, and they are the same control either way.
 *
 * Empty when the rule does not measure this metric, which is how the page
 * knows to say so instead of drawing a row of dashes.
 */
export function getComparisonVariantRows(
  significance: MarketingRouterSignificance,
  metric: MarketingRouterMetricKey,
): Array<ComparisonVariantRow> {
  const results = significance.results.filter((result) => result.metric === metric);
  const base = results[0];

  if (!base) {
    return [];
  }

  return [
    {
      variantId: significance.baseVariantId,
      variantName: significance.baseVariantName,
      isControl: true,
      visitors: base.baseN,
      value: base.baseValue,
      total: base.baseTotal,
      relativeLift: null,
      result: null,
    },
    ...results.map((result) => ({
      variantId: result.variantId,
      variantName: result.variantName,
      isControl: false,
      visitors: result.treatN,
      value: result.treatValue,
      total: result.treatTotal,
      relativeLift: result.relativeLift,
      result,
    })),
  ];
}

/**
 * A rule svc-promo says failed its sample-ratio check: traffic is not
 * splitting between its variants the way the rule is configured.
 *
 * Its lifts and confidences are still reported, and its own detail page tells
 * the reader they cannot be trusted. A side-by-side that leaves that out says
 * the opposite, by setting those numbers next to a healthy rule's.
 */
export function isComparedRuleUnreliable(entry: ComparisonEntry): boolean {
  return entry.significance?.health.srm.healthy === false;
}

/** The names of the compared rules whose numbers cannot be trusted. */
export function getUnreliableComparedRuleNames(entries: Array<ComparisonEntry>): Array<string> {
  return entries.filter(isComparedRuleUnreliable).map((entry) => entry.rule.name);
}

/** A reading svc-promo itself marked invalid, whatever its verdict says. */
export function isMetricResultUnreliable(result: MarketingRouterMetricResult | null): boolean {
  return result !== null && result.invalidReason !== undefined;
}

/** Why a rule shows no rows for the selected metric. */
export function getMissingMetricReason(entry: ComparisonEntry, metric: MarketingRouterMetricKey): string {
  if (!entry.significance) {
    return 'Results are not available for this rule.';
  }

  return `This rule is not measured on ${METRIC_DISPLAY[metric].proseName}.`;
}
